import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { StylePresetSelector } from './StylePresetSelector';
import { VoiceLibraryBrowser } from '../voices/VoiceLibraryBrowser';
import type { BookDto } from '../../api/queries/books';
import { useUpdateBookSettings } from '../../api/queries/voices';

interface BookSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  bookId: string;
  book: BookDto;
}

const POV_OPTIONS = [
  'first-person',
  'second-person',
  'third-person-limited',
  'third-person-omniscient',
] as const;
const TENSE_OPTIONS = ['past', 'present', 'future'] as const;
const NARRATIVE_STYLE_OPTIONS = [
  'descriptive',
  'concise',
  'literary',
  'cinematic',
  'conversational',
] as const;

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

/**
 * Book-level settings dialog with General, Style, and Narration tabs.
 * General mirrors book metadata fields (title, genre, POV, tense, narrative style).
 * Style holds art style presets. Narration configures the default narrator voice.
 *
 * Currently state is local/UI-only for style and narration settings — these will
 * be persisted to book metadata (JSONB) once the backend endpoint is available.
 */
export function BookSettingsDialog({
  open,
  onClose,
  bookId,
  book,
}: BookSettingsDialogProps) {
  const [tab, setTab] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  // General tab state
  const [title, setTitle] = useState(book.title);
  const [description, setDescription] = useState(book.description ?? '');
  const [genre, setGenre] = useState(book.genre ?? '');
  const [pov, setPov] = useState(book.pov);
  const [tense, setTense] = useState(book.tense);
  const [narrativeStyle, setNarrativeStyle] = useState(
    book.narrativeStyle ?? '',
  );

  // Style tab state
  const [artStylePreset, setArtStylePreset] = useState(book.artStyle ?? '');

  // Narration tab state
  const [defaultVoiceId, setDefaultVoiceId] = useState(
    book.narratorVoiceId ?? '',
  );
  const [defaultVoiceName, setDefaultVoiceName] = useState('');
  const [voiceBrowserOpen, setVoiceBrowserOpen] = useState(false);

  const updateSettings = useUpdateBookSettings(bookId);

  const handleSelectVoice = useCallback(
    (voiceId: string, voiceName: string) => {
      setDefaultVoiceId(voiceId);
      setDefaultVoiceName(voiceName);
    },
    [],
  );

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        artStyle: artStylePreset || null,
        narratorVoiceId: defaultVoiceId || null,
      });
      setSnackMessage('Book settings saved');
    } catch {
      setSnackMessage('Failed to save settings');
    }
    setSnackOpen(true);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Book Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, v: number) => setTab(v)}>
              <Tab label="General" />
              <Tab label="Style" />
              <Tab label="Narration" />
            </Tabs>
          </Box>

          {/* General Tab */}
          <TabPanel value={tab} index={0}>
            <Stack spacing={2}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Genre"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                size="small"
                fullWidth
                placeholder="e.g., Fantasy, Sci-Fi, Romance"
              />
              <FilterableSelect
                options={POV_OPTIONS.map((option) => ({
                  value: option,
                  label: option,
                }))}
                value={pov}
                onChange={(newValue) => setPov(newValue)}
                label="Point of View"
                allowEmpty={false}
                size="small"
              />
              <FilterableSelect
                options={TENSE_OPTIONS.map((option) => ({
                  value: option,
                  label: option,
                }))}
                value={tense}
                onChange={(newValue) => setTense(newValue)}
                label="Tense"
                allowEmpty={false}
                size="small"
              />
              <FilterableSelect
                options={NARRATIVE_STYLE_OPTIONS.map((option) => ({
                  value: option,
                  label: option,
                }))}
                value={narrativeStyle}
                onChange={(newValue) => setNarrativeStyle(newValue)}
                label="Narrative Style"
                allowEmpty={true}
                emptyLabel="None"
                size="small"
              />
            </Stack>
          </TabPanel>

          {/* Style Tab */}
          <TabPanel value={tab} index={1}>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Art style preset applied to image generation for the entire
                book.
              </Typography>
              <StylePresetSelector
                value={artStylePreset}
                onChange={setArtStylePreset}
              />
              <Button
                variant="outlined"
                size="small"
                component="label"
                sx={{ alignSelf: 'flex-start' }}
              >
                Upload style reference image
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={() => {
                    setSnackMessage('Style reference image upload coming soon');
                    setSnackOpen(true);
                  }}
                />
              </Button>
            </Stack>
          </TabPanel>

          {/* Narration Tab */}
          <TabPanel value={tab} index={2}>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Set a default narrator voice for this book. Chapters may
                override.
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <RecordVoiceOverIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Default voice:{' '}
                  <strong>{defaultVoiceName || 'None selected'}</strong>
                  {defaultVoiceId && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.disabled"
                      sx={{ ml: 1 }}
                    >
                      ({defaultVoiceId})
                    </Typography>
                  )}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setVoiceBrowserOpen(true)}
                >
                  Choose voice
                </Button>
                {defaultVoiceId && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setDefaultVoiceId('');
                      setDefaultVoiceName('');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Chapter-level voice overrides will be configurable from the
                chapter outline once the backend supports it.
              </Typography>
            </Stack>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <VoiceLibraryBrowser
        open={voiceBrowserOpen}
        onClose={() => setVoiceBrowserOpen(false)}
        onSelectVoice={handleSelectVoice}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />
    </>
  );
}
